import { useNavigate } from "react-router-dom";
import { FaChevronRight, FaChevronLeft } from "react-icons/fa";
import React, { useCallback, useEffect, useRef, useState } from "react";
import RecomendedItem from "../Components/RecomendedItem";
import RecomItems from "../Components/RecomItems";
import SubCategoryItems from "../Components/SubCategoryItems";
import { _get, _post } from "../services/apiClient";
import { useLocation } from "react-router-dom";
import GuestDetailsModal from "../Components/GuestDetailsModal";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useCategoryAnimation } from "../hooks/useCategoryAnimation";
import FloatingDecorations from "../Components/HomeDecorations/FloatingDecorations";
import MenuSectionDecorations from "../Components/HomeDecorations/MenuSectionDecorations";
import { CategorySkeleton, BannerSkeleton } from "../Components/SkeletonLoader";
import { useRipple } from "../hooks/useRipple";
import { getAppConfig } from "../utilis/appConfig";
import MenuItem from "../Components/MenuItem";
import { useRestaurantDetails } from "../context/RestaurantDetailsContext";
import ServiceUnavailable from "../Components/ServiceUnavailable";

function Home({ cart, setCart, handleClick, restaurantStatus }) {
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const observerRef = useRef(null);
  const loaderRef = useRef(null);
  const [banners, setBanners] = useState([]);
  const [subCategories, setsubCategories] = useState([]);
  const [hovered, setHovered] = useState(false);
  const toggleHover = () => setHovered(!hovered);
  const [showAll, setShowAll] = useState(false);
  const [visibleCount, setVisibleCount] = useState(6);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingBanners, setLoadingBanners] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const [categorySearch, setCategorySearch] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [isProductSearching, setIsProductSearching] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { defaultImage, logo } = getAppConfig();
  const { restaurantDetails } = useRestaurantDetails();

  const getLocalizedText = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    if (typeof field === "object")
      return field[i18n.language] || field.en || "";
    return "";
  };

  const createRipple = useRipple();

  const setCategoryRef = useCategoryAnimation();

  const queryParams = new URLSearchParams(location.search);
  const tableId = queryParams.get("tableId");
  const restaurantId = queryParams.get("restaurantId");
  const isQrEntry = Boolean(tableId && restaurantId);

  //const displayedCategories = showAll ? categories : categories.slice(0, 10);

  // Scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  useEffect(() => {
    if (!isQrEntry) return;

    const params = new URLSearchParams(location.search);
    const isShareLink = params.has("share");

    const guestInfo = localStorage.getItem("guestInfo");

    if (!guestInfo && !isShareLink) {
      setShowGuestModal(true);
    } else {
      setShowGuestModal(false);
    }
  }, [isQrEntry, location.search]);

  useEffect(() => {
    if (isQrEntry) {
      localStorage.setItem(
        "qrSession",
        JSON.stringify({ restaurantId, tableId }),
      );
    }
  }, [isQrEntry, restaurantId, tableId]);

  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleCount < categories.length) {
          setVisibleCount((prev) => prev + 6); // load 6 more
        }
      },
      { threshold: 1.0 },
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [categories, visibleCount]);

  const displayedCategories = categories.slice(0, visibleCount);

  useEffect(() => {
    const term = categorySearch.trim();
    if (!term) {
      setProductResults([]);
      setIsProductSearching(false);
      return;
    }
    setIsProductSearching(true);
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        params.set("search", term);
        params.set("page", 1);
        params.set("limit", 30);
        if (isQrEntry) {
          params.set("mode", "dinein");
          params.set("tableId", tableId);
          params.set("restaurantId", restaurantId);
        }
        const res = await _get(`/products/menu?${params.toString()}`);
        console.log("[Home Search]", term, "→", res.data.products?.length ?? 0, "results");
        setProductResults(res.data.products || []);
      } catch (err) {
        console.error("[Home Search] API error:", err);
        setProductResults([]);
      } finally {
        setIsProductSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [categorySearch]);

  const fetchBannerData = async () => {
    try {
      setLoadingBanners(true);
      const response = await _get(`/banners`);
      const currentTime = new Date();

      const activeBanners = response.data.filter((b) => {
        const startTime = new Date(b.startDate);
        const endTime = new Date(b.endDate);

        return startTime <= currentTime && endTime > currentTime;
      });

      setBanners(activeBanners.length > 0 ? activeBanners : []);
    } catch (error) {
      console.error("Error fetching banner:", error);
    } finally {
      setLoadingBanners(false);
    }
  };

  useEffect(() => {
    fetchBannerData();
    const interval = setInterval(fetchBannerData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchCategoriesData = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const [response, offersCountRes, todaysSpecialCountRes, combosCountRes] = await Promise.all([
        _get(`/categories`),
        (async () => {
          const params = new URLSearchParams();
          params.set("category", "offers");
          params.set("page", "1");
          params.set("limit", "1");
          if (isQrEntry) {
            params.set("mode", "dinein");
            params.set("tableId", tableId);
            params.set("restaurantId", restaurantId);
          }
          try {
            return await _get(`/products/menu?${params.toString()}`);
          } catch (e) {
            console.error("Error fetching offers count:", e);
            return null;
          }
        })(),
        (async () => {
          const params = new URLSearchParams();
          params.set("category", "todays_special");
          params.set("page", "1");
          params.set("limit", "1");
          if (isQrEntry) {
            params.set("mode", "dinein");
            params.set("tableId", tableId);
            params.set("restaurantId", restaurantId);
          }
          try {
            return await _get(`/products/menu?${params.toString()}`);
          } catch (e) {
            console.error("Error fetching todays special count:", e);
            return null;
          }
        })(),
        (async () => {
          const params = new URLSearchParams();
          params.set("category", "combos");
          params.set("page", "1");
          params.set("limit", "1");
          if (isQrEntry) {
            params.set("mode", "dinein");
            params.set("tableId", tableId);
            params.set("restaurantId", restaurantId);
          }
          try {
            return await _get(`/products/menu?${params.toString()}`);
          } catch (e) {
            console.error("Error fetching combos count:", e);
            return null;
          }
        })(),
      ]);
      const fetchedCategories = response.data || [];
      const offerProductCount = offersCountRes?.data?.totalCount ?? 0;
      const offersCategory =
        offerProductCount > 0
          ? [
              {
                _id: "offers",
                name: t("offers"),
                images: [],
              },
            ]
          : [];
      
      const todaysSpecialCount = todaysSpecialCountRes?.data?.totalCount ?? 0;
      const todaysSpecialCategory =
        todaysSpecialCount > 0
          ? [
              {
                _id: "todays_special",
                name: t("todaysSpecial") || "Today's Special",
                images: [],
              },
            ]
          : [];

      const combosCount = combosCountRes?.data?.totalCount ?? 0;
      const combosCategory =
        combosCount > 0
          ? [
              {
                _id: "combos",
                name: t("combos") || "Combos",
                images: [],
              },
            ]
          : [];

      setCategories([
        ...todaysSpecialCategory,
        ...offersCategory,
        ...combosCategory,
        ...fetchedCategories,
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingCategories(false);
    }
  }, [isQrEntry, tableId, restaurantId, t]);

  useEffect(() => {
    fetchCategoriesData();
  }, [fetchCategoriesData]);

  // Custom arrow components for the slider
  const PrevArrow = ({ onClick }) => (
    <button
      onClick={onClick}
      className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-6 z-10 w-10 h-10 sm:w-14 sm:h-14 bg-white/90 backdrop-blur-sm rounded-full shadow-xl flex items-center justify-center hover:bg-orange-500 hover:shadow-2xl transition-all duration-300 border-2 border-orange-200 hover:border-orange-500 group"
    >
      <FaChevronLeft className="text-orange-600 text-base sm:text-xl group-hover:text-white transition-colors" />
    </button>
  );

  const NextArrow = ({ onClick }) => (
    <button
      onClick={onClick}
      className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-6 z-10 w-10 h-10 sm:w-14 sm:h-14 bg-white/90 backdrop-blur-sm rounded-full shadow-xl flex items-center justify-center hover:bg-orange-500 hover:shadow-2xl transition-all duration-300 border-2 border-orange-200 hover:border-orange-500 group"
    >
      <FaChevronRight className="text-orange-600 text-base sm:text-xl group-hover:text-white transition-colors" />
    </button>
  );

  const settings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    dotsClass: "slick-dots !relative !bottom-0 !mt-10",
    customPaging: (i) => (
      <div className="w-3 h-3 rounded-full bg-orange-200 hover:bg-orange-500 transition-all duration-300 hover:scale-125"></div>
    ),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
        },
      },
    ],
  };

  const normalizeId = (val) => {
    if (!val) return null;
    if (typeof val === "string") return val;
    if (typeof val === "object" && val.$oid) return val.$oid;
    if (typeof val === "object" && val._id) return val._id;
    return val;
  };

  const handleBannerClick = async (banner) => {
    const productId = normalizeId(banner?.productId);
    if (!productId) return;

    try {
      const res = await _get(`/products/${productId}`);
      const product = res?.data;
      const categoryId = normalizeId(
        product?.category?._id || product?.category,
      );
      const subCatId = normalizeId(
        product?.subcategory?._id || product?.subcategory,
      );
      if (!categoryId) return;

      const params = new URLSearchParams(location.search);
      if (subCatId) params.set("subcat", subCatId);

      navigate({
        pathname: `/products/${categoryId}`,
        search: params.toString() ? `?${params.toString()}` : location.search,
      });
    } catch (error) {
      console.error("Error opening banner product:", error);
    }
  };
  const handleGuestSubmit = async ({ name, mobile, count }) => {
    try {
      const payload = {
        restaurantId,
        tableId,
        name: name.trim(),
        phone: mobile.trim(),
        customerCount: Number(count),
      };

      const res = await _post("/table/request", payload);
      const allocation = res?.data?.allocation;

      if (!allocation?._id) {
        throw new Error("Allocation ID not received");
      }

      localStorage.setItem("allocationId", allocation._id);

      localStorage.setItem(
        "guestInfo",
        JSON.stringify({
          ...payload,
          source: "qr",
        }),
      );

      setShowGuestModal(false);

      enqueueSnackbar("Request sent to restaurant", {
        variant: "success",
      });

      return null; // ✅ success, no error
    } catch (err) {
      const message =
        err?.response?.data?.message || "Unable to submit request. Try again.";

      return message; // ✅ return error to modal
    }
  };
  const handleJoinWithToken = async (token) => {
    try {
      const res = await _post("/allocation/joinroom", { token });

      localStorage.setItem("allocationId", res.data.allocationId);
      localStorage.setItem("guestInfo", JSON.stringify(res.data));

      enqueueSnackbar("Joined group order successfully", {
        variant: "success",
      });

      setShowGuestModal(false);
      return null; // ✅ success
    } catch (err) {
      return err?.response?.data?.message || "Invalid or expired token";
    }
  };

  if (restaurantDetails && restaurantDetails.amcStatus && restaurantDetails.amcStatus !== "active") {
    return <ServiceUnavailable />;
  }

  return (
    <>
      <GuestDetailsModal
        open={showGuestModal}
        onSubmit={handleGuestSubmit}
        onJoinWithToken={handleJoinWithToken}
      />

      <div className="w-full bg-gradient-to-b from-orange-50 to-amber-50 relative overflow-hidden">
        {/* Floating Food Decorations */}
        <FloatingDecorations />

        <SubCategoryItems recommendedItemLists={subCategories} cart={cart} />

        {/* Offline Status Banner */}
        {restaurantStatus === "offline" && (
          <div className="w-full bg-red-50 border-y border-red-200">
            <div className="w-full px-5 sm:px-8 md:px-12 lg:px-16 xl:px-20 py-4">
              <p className="text-red-700 text-center text-sm font-semibold">
                ⚠️ Currently we are not accepting any online orders
              </p>
            </div>
          </div>
        )}

        {/* Featured Items Section */}
        {(loadingBanners || banners.length > 0) && (
          <section className="w-full bg-gradient-to-b from-white to-orange-50 py-12 sm:py-16">
            <div className="w-full px-2 sm:px-8 md:px-12 lg:px-16 xl:px-20">
              <div className="text-center mb-8 sm:mb-10">
                <button
                  onClick={(e) => {
                    createRipple(e);
                    document
                      .getElementById("category-section")
                      ?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                  }}
                  className="ripple-container inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full text-orange-700 text-sm font-bold mb-6 shadow-sm hover:shadow-md hover:from-orange-200 hover:to-amber-200 transition-all duration-300 cursor-pointer tap-scale"
                >
                  <span className="text-lg">🍽️</span> {t("viewFullMenu")}
                </button>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-orange-900 mb-4 tracking-tight">
                  {t("featuredItems")}
                </h2>
                <p className="text-orange-600 text-base sm:text-lg lg:text-xl max-w-2xl mx-auto font-medium">
                  {t("featuredItemsDesc")}
                </p>
              </div>
              <div className="relative px-0 sm:px-12">
                {loadingBanners ? (
                  <div className="grid grid-cols-1 gap-4">
                    {[1, 2, 3].map((i) => (
                      <BannerSkeleton key={i} />
                    ))}
                  </div>
                ) : (
                  <Slider {...settings}>
                    {[...Array(Math.max(3, banners.length))].map((_, index) => {
                      const banner = banners[index % banners.length];
                      const isClickable = Boolean(
                        normalizeId(banner?.productId),
                      );
                      return (
                        <div key={index} className="px-1 py-2 sm:px-3">
                          <div
                            className={`bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] border-2 border-orange-100 hover:border-orange-200 group ${isClickable ? "cursor-pointer" : ""
                              }`}
                            onClick={() => {
                              if (!isClickable) return;
                              handleBannerClick(banner);
                            }}
                          >
                            <div className="relative w-full overflow-hidden rounded-xl h-40 sm:h-[220px]">
                              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>
                              <img
                                src={
                                  banner.imagePath
                                    ? `${process.env.REACT_APP_IMAGE_BASE_URL}${banner.imagePath.startsWith("/") ? banner.imagePath : `/${banner.imagePath}`
                                    }`
                                    : defaultImage
                                      ? `${process.env.REACT_APP_IMAGE_BASE_URL}${defaultImage}`
                                      : "/img/finglider_logo.jpg"
                                }
                                alt={`Banner ${index + 1}`}
                                loading="lazy"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = defaultImage
                                    ? `${process.env.REACT_APP_IMAGE_BASE_URL}${defaultImage}`
                                    : "/img/finglider_logo.jpg";
                                }}
                                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                              />
                              {/* Preview button — appears on hover */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewImg(
                                    `${process.env.REACT_APP_IMAGE_BASE_URL}${banner.imagePath.startsWith("/") ? banner.imagePath : `/${banner.imagePath}`}`,
                                  );
                                }}
                                className="absolute bottom-2 end-2 z-20 bg-white/80 hover:bg-white text-gray-700 rounded-full p-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 shadow-md"
                                title="View full image"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                                  />
                                </svg>
                              </button>
                            </div>
                            {banner.description && (
                              <div className="p-6 bg-white border-t border-orange-100">
                                <p className="text-orange-900 text-base font-semibold leading-relaxed">
                                  {banner.description}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </Slider>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Browse By Category Section */}
        <section
          id="category-section"
          className="w-full bg-gradient-to-b from-amber-50 to-orange-50 py-16 sm:py-24 min-h-screen relative overflow-hidden"
        >
          {/* Additional floating decorations for menu section */}
          <MenuSectionDecorations />

          <div className="w-full px-5 sm:px-8 md:px-12 lg:px-16 xl:px-20 relative z-10">
            <div className="text-center mb-12 sm:mb-16">
              <span className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full text-orange-700 text-sm font-bold mb-6 shadow-sm">
                <span className="text-lg">🍴</span>
                {t("ourMenu")}
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-orange-900 mb-4 tracking-tight">
                {t("exploreOurMenu")}
              </h2>
              <p className="text-orange-600 text-base sm:text-lg lg:text-xl max-w-2xl mx-auto font-medium mb-4">
                {t("exploreOurMenuDesc")}
              </p>
              <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-orange-400 to-transparent mx-auto"></div>
            </div>

            {/* Category Search Bar */}
            <div className="relative max-w-lg mx-auto mb-10">
              <div className="relative flex items-center">
                <svg
                  className="absolute start-4 w-5 h-5 text-orange-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  placeholder={t("searchPlaceholder")}
                  className="w-full ps-12 pe-10 py-3.5 bg-white border-2 border-orange-100 focus:border-orange-400 rounded-2xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none shadow-sm transition-colors duration-200"
                />
                {categorySearch && (
                  <button
                    type="button"
                    onClick={() => setCategorySearch("")}
                    className="absolute end-3 text-gray-400 hover:text-orange-500 transition-colors"
                    aria-label="Clear search"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {categorySearch.trim() ? (
              <>
                {isProductSearching ? (
                  <div className="flex justify-center py-16">
                    <div className="w-10 h-10 border-2 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
                  </div>
                ) : productResults.length > 0 ? (
                  <>
                    <p className="text-sm text-orange-600 font-medium mb-4">
                      {productResults.length} product{productResults.length !== 1 ? "s" : ""} found for &ldquo;{categorySearch}&rdquo;
                    </p>
                    <div className="flex flex-wrap">
                      {productResults.map((product) => (
                        <MenuItem
                          key={product._id}
                          MenuItem={product}
                          handleClick={handleClick}
                          products={productResults}
                          cart={cart}
                          setCart={setCart}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                      <svg
                        className="w-8 h-8 text-orange-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-orange-900 font-semibold text-base mb-1">
                      No products found
                    </p>
                    <p className="text-orange-400 text-sm">
                      Try a different search term
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-5 lg:gap-6">
                  {loadingCategories
                    ? [...Array(6)].map((_, index) => (
                        <CategorySkeleton key={`skeleton-${index}`} />
                      ))
                    : displayedCategories.map((item, index) => (
                        <div
                          key={item._id}
                          ref={setCategoryRef(index)}
                          className="group cursor-pointer scroll-animate hover-lift"
                          style={{ "--delay": `${index * 0.05}s` }}
                          onClick={(e) => {
                            createRipple(e);
                            navigate({
                              pathname: `/products/${item._id}`,
                              search: location.search,
                            });
                          }}
                        >
                          <div className="ripple-container bg-gray-50 rounded-2xl sm:rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 border border-orange-100 hover:border-orange-300 h-full flex flex-col">
                            <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-orange-50 to-amber-50">
                              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                              <img
                                src={
                                  item.images?.[0]
                                    ? `${process.env.REACT_APP_IMAGE_BASE_URL}${item.images[0]}`
                                    : defaultImage
                                      ? `${process.env.REACT_APP_IMAGE_BASE_URL}${defaultImage}`
                                      : "/img/finglider_logo.jpg"
                                }
                                alt={getLocalizedText(item.name)}
                                loading="lazy"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                onError={(e) => {
                                  const img = e.currentTarget;
                                  img.onerror = null;
                                  img.src = defaultImage
                                    ? `${process.env.REACT_APP_IMAGE_BASE_URL}${defaultImage}`
                                    : "/img/finglider_logo.jpg";
                                  img.style.objectFit = "contain";
                                  img.style.backgroundColor = "#fff";
                                }}
                              />
                            </div>
                            <div className="p-3 sm:p-4 bg-gradient-to-b from-gray-50 to-orange-50/20 flex items-center justify-center min-h-[52px] sm:min-h-[60px]">
                              <h3 className="text-orange-900 text-xs sm:text-sm font-bold text-center leading-tight line-clamp-2">
                                {getLocalizedText(item.name)}
                              </h3>
                            </div>
                          </div>
                        </div>
                      ))}
                </div>

                <div
                  ref={loaderRef}
                  className="h-20 flex items-center justify-center"
                >
                  {!loadingCategories && visibleCount < categories.length && (
                    <div className="w-8 h-8 border-2 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
                  )}
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-20 sm:bottom-6 left-4 sm:left-6 z-[70] w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white rounded-full shadow-2xl hover:shadow-orange-500/50 transform hover:scale-110 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center group ring-2 ring-orange-200 ring-offset-2 hover:animate-pulse-glow"
          aria-label="Scroll to top"
        >
          <svg
            className="w-5 h-5 sm:w-6 sm:h-6 transform group-hover:-translate-y-0.5 group-hover:scale-110 transition-transform duration-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}

      {/* Banner full-image lightbox */}
      {previewImg && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm"
          onClick={() => setPreviewImg(null)}
        >
          <div
            className="relative max-w-3xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewImg(null)}
              className="absolute -top-10 end-0 text-white/80 hover:text-white transition-colors text-3xl leading-none"
              aria-label="Close preview"
            >
              ✕
            </button>
            <img
              src={previewImg}
              alt="Banner preview"
              className="w-full h-auto max-h-[80vh] object-contain rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </>
  );
}

export default Home;
